import {
  IonContent,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
} from '@ionic/react';
import { matchPath, useLocation } from 'react-router-dom';
import './Menu.css';
import { isStaff } from './extras/Functions';
import { useAppSelector } from '../Hooks';
import { selectUser } from '../reducers/UserReducer';
import { default as dashboard_icon} from "../AE2/static/icons/menu/Dashboard.svg"
import { default as customers_icon} from "../AE2/static/icons/menu/Customers.svg"
import { default as resources_icon} from "../AE2/static/icons/menu/Resources.svg"
import { default as alpha_white_icon} from "../AE2/static/icons/menu/Alpha Insights Logo-White.svg"
import { default as collapse_icon} from "../AE2/static/icons/menu/Collapse.svg"
import { default as expand_icon} from "../AE2/static/icons/menu/Expand.svg"
import { default as alpha_a_icon} from "../AE2/static/icons/menu/Alpha Insights Secondary Logo-White.svg"
import { default as settings_icon} from "../AE2/static/icons/menu/Settings.svg"
import { default as add_login_icon} from "../AE2/static/icons/menu/Add Login.svg"
import { default as add_customer_icon} from "../AE2/static/icons/menu/Add Customer.svg"
import { default as tracking_icon} from "../AE2/static/icons/menu/Tracking.svg"
import { default as service_estimate_icon} from "../AE2/static/icons/menu/Service-Estimates.svg"
import { default as buildings_icon} from "../AE2/static/icons/customer_page/Main Menu/Buildings.svg"
import { default as performance_icon} from "../AE2/static/icons/customer_page/Main Menu/Performance.svg"
import { default as recommendations_icon} from "../AE2/static/icons/customer_page/Main Menu/Recommendations.svg"
import { default as building_placeholder} from "../AE2/static/icons/customer_page/Building Performance Overview/Building Photo Placeholder.svg"
import { default as customer_logo_placeholder} from "../AE2/static/icons/customer_page/Main Menu/Customer Image Placeholder.svg"
import Logout from './auth/Logout';
import { useState } from 'react';
import { REACT_APP_AWS_BUCKET_NAME, REACT_APP_AWS_REGION } from '../actions/settings';
import { isMobile } from 'react-device-detect';
import { gitPullRequest } from 'ionicons/icons';
import usePermissionGroups from '../utils/usePermissionGroups';

interface props{
    collapsedMenu:Boolean,
    toggleCollapsedMenu: ()=>void
}

interface AppPage {
  url: string;
  iconUrl: string;
  title: string;
  staff: boolean;
  customerOnly?: boolean;
  permission?:string[];
}

const appPages: AppPage[] = [
  {
    title: 'Dashboard',
    url: '/home',
    iconUrl:dashboard_icon,
    staff: false
  },
  // {
  //   title: 'Buildings',
  //   url: '/buildings_performance',
  //   iconUrl:buildings_icon,
  //   staff: false,
  //   customerOnly: true
  // },
  // {
  //   title: 'Performance',
  //   url: '/buildings_monthly_performance',
  //   iconUrl:performance_icon,
  //   staff: false,
  //   customerOnly: true
  // },
  // {
  //   title: 'Recommendations',
  //   url: '/recommendations', // TODO: no route exists yet
  //   iconUrl:recommendations_icon,
  //   staff: false,
  //   customerOnly: true
  // },
  {
    title: 'Resources',
    url: '/resources',
    iconUrl:resources_icon,
    staff: false,
    customerOnly: true
  },
  {
    title: 'Customers',
    url: '/customers',
    iconUrl:customers_icon,
    staff:true
  },
  {
    title: 'Tracking',
    url: '/tracking',
    iconUrl:tracking_icon,
    staff:true
  },
  {
    title: 'Service Estimates',
    url: '/service_estimates',
    iconUrl:service_estimate_icon,
    staff:true,
    permission: ['Service','ServiceEstimates','Admin','Director']
  },
  {
    title: 'Resources',
    url: '/resources',
    iconUrl:resources_icon,
    staff:true
  },
  {
    title: 'Pull Data',
    url: '/data-operations',
    iconUrl:gitPullRequest,
    staff:true,
    permission: ["Admin","Supervisor"]
  },
  {
    title: 'Add Customer',
    url: '/customer',
    iconUrl:add_customer_icon,
    staff:true,
    permission: ["Supervisor", "Admin", "Employee"]
  },
  {
    title: 'Create Login',
    url: '/account?tab=new_user',
    iconUrl:add_login_icon,
    staff:true,
    permission:['createAE2CustomerAccount']
  },
  
//   {
//     title: 'Analytics',
//     url: '/analytics',
//     iconUrl:'../assets/es.png',
//     staff:true
//   },
];

type MenuImageTarget = {type: 'customer' | 'building', id: string}

// Image from the same S3 thumbnail location the customer and building pages use, with a placeholder if it has none
const MenuImage: React.FC<MenuImageTarget> = ({type, id}) => {
  const [failed, setFailed] = useState(false);
  const src = `https://${REACT_APP_AWS_BUCKET_NAME}.s3.${REACT_APP_AWS_REGION}.amazonaws.com/thumbnails/pics/${id}-${type}-img`;
  const placeholder = type === 'building' ? building_placeholder : customer_logo_placeholder;

  return (
    <div style={{display:'flex', justifyContent:'center', padding:'1rem'}}>
      <IonImg
        src={failed ? placeholder : src}
        alt={`${type} image`}
        style={{width:'80%'}}
        onIonError={() => setFailed(true)}
      />
    </div>
  );
};

// Pages whose URL carries a customer or building id, used to pick the image shown in the menu
const CUSTOMER_PATHS = [
  '/customer/:id/edit',
  '/customer/:id/addproperty',
  '/customer/:id/addcampus',
  '/customer/:id/controls_versioning',
  '/customer_buildings/monthly_performance/:id',
  '/buildings/performance/:id', // the customer yearly performance page links here with a customer id
];

const BUILDING_PATHS = [
  '/building/:id/edit',
  '/building/:id/utility_service',
  '/building/:id/meters',
  '/building/monthly_performance/:id',
];

const imageTargetFromLocation = (location: {pathname: string, search: string}): MenuImageTarget | null => {
  const params = new URLSearchParams(location.search);
  const id = params.get('id');
  if (id && params.get('sec') === 'customers') {
    return {type: 'customer', id};
  }
  if (id && params.get('sec') === 'buildings') {
    return {type: 'building', id};
  }
  const lists: [MenuImageTarget['type'], string[]][] = [['customer', CUSTOMER_PATHS], ['building', BUILDING_PATHS]];
  for (const [type, paths] of lists) {
    for (const path of paths) {
      const match = matchPath<{id: string}>(location.pathname, {path, exact: true});
      if (match) {
        return {type, id: match.params.id};
      }
    }
  }
  return null;
};

const MenuV2: React.FC<props> = ({collapsedMenu, toggleCollapsedMenu}) => {
  const location = useLocation();
  const user = useAppSelector(selectUser)
  const [imageKey, setImageKey] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const {hasPermission} = usePermissionGroups()
  

  // Show the image of the customer or building whose page is open. Customers fall back to their own
  // image on pages that don't belong to a specific customer or building; staff see nothing there.
  const imageTarget: MenuImageTarget | null = imageTargetFromLocation(location)
    ?? (!isStaff(user) && user.customer_id ? {type: 'customer', id: user.customer_id} : null);

  const handleClick = () =>{
    window.location.href = '/'
  }

  const handleToggle = () => {
    setIsTransitioning(true);
    toggleCollapsedMenu();
    setImageKey(prev => prev + 1);
    setTimeout(() => setIsTransitioning(false), 50);
    
  }


  return (
    <IonMenu contentId="main" type="overlay">
      <IonContent className='bg-navy'>
        <div className='h-100' style={{display:'flex', flexDirection:'column',justifyContent:'space-between'}}>
        <IonList className='bg-transparent'>
          <div className='menu-logo-container'>
            {/* <AE2Icon className='ae2-logo' onClick={() => handleClick()}/> */}
            <IonImg key={imageKey} className="ae2_banner" style={{width:'100%'}} onClick={() => handleClick()} src={collapsedMenu ?  alpha_a_icon : alpha_white_icon}/>
            <div className='collapse_icon' >
                <IonIcon hidden={isMobile} onClick={()=>handleToggle()} src={collapsedMenu ? expand_icon :collapse_icon}></IonIcon>
            </div>
          </div>

          {imageTarget && !collapsedMenu ? <MenuImage key={`${imageTarget.type}-${imageTarget.id}`} type={imageTarget.type} id={imageTarget.id}/> : null}

          {appPages.map((appPage, index) => {

            if(appPage.customerOnly && isStaff(user)){
              return null
            }
            if(!appPage.staff || (appPage.staff && isStaff(user))){
              if (isTransitioning) {
                //prevents buttons showing up to soon for a split second
                return null;
              }
              if(appPage.permission &&  !hasPermission(appPage.permission) ){
                return null
              }
            return(                         
                    collapsedMenu ? 
                    <IonItem key={index} className={location.pathname===appPage.url ? ' icon-only-item bg-cobalt-blue bg-hover-navy': ' icon-only-item bg-hover-navy bg-transparent'} routerLink={appPage.url} routerDirection="none" lines="none" detail={false}>
                        <IonIcon  src={appPage.iconUrl}/>
                    </IonItem>  :              
                    <IonItem key={index} className={location.pathname===appPage.url ? 'bg-cobalt-blue bg-hover-white': 'bg-transparent bg-hover-white'}  routerLink={appPage.url} routerDirection="none" lines="none" detail={false}>
                        <IonIcon slot='start' src={appPage.iconUrl}/>
                        <IonLabel className='text-white font-helvetica font-16pt'>{appPage.title}</IonLabel>
                    </IonItem>       
          );
            }
            else{
              return null
            }
          })}

        </IonList>
        <IonList className='bg-transparent'>
          {isTransitioning ? null : collapsedMenu ? 
          <><IonItem className=' icon-only-item bg-transparent pointer bg-hover-navy' routerLink={''} routerDirection="none" lines="none" detail={false}>
                <IonIcon src={settings_icon} />
              </IonItem><IonItem className='bg-transparent bg-hover-navy'>
                  <Logout collapsed={collapsedMenu}/>
                </IonItem></> : 
          <><IonItem className='bg-transparent' routerLink={''} routerDirection="none" lines="none" detail={false}>
              <IonItem className='bg-transparent bg-hover-navy pointer' >
                <IonIcon slot='start' src={settings_icon} />
                <IonLabel className='text-white text-hover-black font-helvetica font-16pt'>Settings</IonLabel>
              </IonItem>
              </IonItem><IonItem className='bg-transparent'>
                  <Logout collapsed={collapsedMenu}/>
                </IonItem></>}
        </IonList>
        </div>
      </IonContent>
    </IonMenu>
  );
};

export default MenuV2;
